/**
 * Generates natural language report text for Maxilla Impaction Sections
 * Based on sample reports structure.
 */

// SECTION 1: POSITION & DEVELOPMENT
export const generatePositionSectionText = (data) => {
    const parts = [];
    const tooth = data.tooth || '13';

    if (data.development && data.eruption) {
        // Check if development is a full sentence starting with "Tooth" or "Crown"
        const devLower = data.development.toLowerCase();
        if (devLower.startsWith('tooth') || devLower.startsWith('crown')) {
            parts.push(`${data.development} and is ${data.eruption.toLowerCase()}.`);
        } else {
            parts.push(`Tooth ${tooth} appears ${data.development.toLowerCase()} and ${data.eruption.toLowerCase()}.`);
        }
    } else if (data.development) {
        const devLower = data.development.toLowerCase();
        if (devLower.startsWith('tooth') || devLower.startsWith('crown')) {
            parts.push(`${data.development}.`);
        } else {
            parts.push(`Tooth ${tooth} appears ${data.development.toLowerCase()}.`);
        }
    } else if (data.eruption) {
        parts.push(`Tooth ${tooth} appears ${data.eruption.toLowerCase()}.`);
    }

    if (data.rotation && data.rotation !== 'None') {
        parts.push(`Tooth ${tooth} shows ${data.rotation} Rotation.`);
    }

    return parts.join(' ');
};

// SECTION 2: IMPACTION DETAILS (Angulation/Winter's)
export const generateImpactionDetailsText = (data) => {
    const parts = [];
    const tooth = data.tooth || '13';
    const type = data.type;
    const mesioAngle = data.mesioAngle;
    const distoAngle = data.distoAngle;

    if (type === 'Mesioangular' && mesioAngle) {
        parts.push(`The long axis of ${tooth} is mesially inclined at an angulation of ${mesioAngle} degrees with the long axis of the adjacent tooth.`);
    } else if (type === 'Distoangular' && distoAngle) {
        parts.push(`The long axis of ${tooth} is distally inclined at an angulation of ${distoAngle} degrees with the long axis of the adjacent tooth.`);
    } else if (type) {
        parts.push(`The impaction is ${type} in nature.`);
    }

    if (data.ectopicLocation) {
        parts.push(`It is ectopically located at ${data.ectopicLocation}.`);
    }

    return parts.join(' ');
};

// SECTION 3: EXTENSIONS
export const generateExtensionText = (data) => {
    const parts = [];

    // Supero-inferiorly
    if (data.extSuperiorMm) {
        parts.push(`Supero-inferiorly, it is extending ${data.extSuperiorMm}mm ${data.extSuperior ? data.extSuperior.toLowerCase() : ''}.`);
    } else if (data.extSuperior) {
        parts.push(`Supero-inferiorly, it is extending ${data.extSuperior.toLowerCase()}.`);
    }

    if (data.extInferiorMm) {
        // pattern check
        parts.push(`Inferiorly extending ${data.extInferiorMm}mm ${data.extInferior ? data.extInferior.toLowerCase() : ''}.`);
    }

    // Mesio-distally
    if (data.extMesial && data.extDistal) {
        parts.push(`Mesio-distally assuming a position from ${data.extMesial.toLowerCase()} to ${data.extDistal.toLowerCase()}.`);
    } else {
        if (data.extMesial) parts.push(`Mesially extending to ${data.extMesial.toLowerCase()}.`);
        if (data.extDistal) parts.push(`Distally extending to ${data.extDistal.toLowerCase()}.`);
    }

    // Buccolingual
    if (data.extBuccolingual) {
        parts.push(`Buccolingual Dimensions: ${data.extBuccolingual}.`);
    }

    return parts.join(' ');
};

// SECTION 4: CROWN
export const generateCrownSectionText = (data) => {
    const parts = [];

    // Shape & Margin
    if (data.crownShape) {
        let msg = `The crown appears ${data.crownShape.toLowerCase()}`;
        if (data.crownMargin) {
            msg += ` with enamel-dentin margins that are ${data.crownMargin.toLowerCase()}`;
        }
        msg += '.';
        parts.push(msg);
    }

    if (data.crownDefect && data.crownDefect !== 'None') {
        parts.push(`Defect noted: ${data.crownDefect}.`);
    }

    if (data.follicularChanges && data.follicularChanges !== 'Normal') {
        parts.push(`Follicular changes: ${data.follicularChanges}.`);
    }

    return parts.join(' ');
};

// SECTION 5: CRESTAL BONE
export const generateCrestalBoneText = (data) => {
    if (data.crestalBone && data.crestalBone !== 'No significant loss') {
        return `Crestal bone assessment: ${data.crestalBone}.`;
    }
    return '';
};

// --- ADJACENT TAB GENERATORS ---

// Helper for row texts
const getRelText = (data, prefix, target) => {
    // Subject + Relation + Object + Position + Effect
    // e.g. "Tip of the crown is in contact with Root..."
    const s = data[`${prefix}Subject`];
    const r = data[`${prefix}Relation`];
    const o = data[`${prefix}Object`];
    const p = data[`${prefix}Position`];
    const e = data[`${prefix}Effect`];

    if (s && r && o) {
        let text = `${s} of ${data.tooth || 'tooth'} is ${r.toLowerCase()} ${o.toLowerCase()} of ${target}`;
        if (p) text += ` positioned ${p.toLowerCase()}`;
        if (e && e !== 'No changes') text += `. Effect: ${e}`;
        else text += '.';
        return text;
    }
    return null;
};

export const generateAdjacentTeethText = (data) => {
    const parts = [];

    const teeth = [
        { key: 'rel11', label: '11' },
        { key: 'rel12', label: '12' },
        { key: 'rel14', label: '14' },
        { key: 'rel15', label: '15' }
    ];

    teeth.forEach(t => {
        const txt = getRelText(data, t.key, t.label);
        if (txt) parts.push(txt);
    });

    if (data.rel53) {
        parts.push(`Relation with 53: ${data.rel53}.`);
    }

    return parts.join(' ');
};

export const generateAnatomicalText = (data) => {
    const parts = [];

    // NPC
    if (data.relNasopalatine) {
        let msg = `Nasopalatine Canal: ${data.relNasopalatine}`;
        if (data.relNasopalatineMm) msg += ` (Bone barrier: ${data.relNasopalatineMm}mm)`;
        parts.push(msg + '.');
    }

    // Nasal Floor
    if (data.relNasalFloor) {
        let msg = `Nasal Floor: ${data.relNasalFloor}`;
        if (data.relNasalFloorMm) msg += ` (Bone barrier: ${data.relNasalFloorMm}mm)`;
        parts.push(msg + '.');
    }

    // Sinus
    if (data.relSinus) {
        let msg = `Maxillary Sinus: ${data.relSinus}`;
        if (data.relSinusMm) msg += ` (Bone barrier: ${data.relSinusMm}mm)`;
        parts.push(msg + '.');
    }

    return parts.join(' ');
};

export const generateDepthText = (data) => {
    if (data.depth) {
        return `Depth of impaction: ${data.depth}.`;
    }
    return '';
};

// --- ROOTS TAB GENERATORS ---

export const generateRootMorphologyText = (data) => {
    const parts = [];

    // 1. General Info
    if (data.number) {
        let msg = `Number of roots: ${data.number}`;
        if (data.twoRootsType) msg += ` (${data.twoRootsType})`;
        if (data.rootNumberDescription) msg += ` - ${data.rootNumberDescription}`;
        msg += '.';
        parts.push(msg);
    }

    if (data.relation) {
        let msg = `Relation of roots: ${data.relation}`;
        if (data.rootRelationDescription) msg += ` - ${data.rootRelationDescription}`;
        msg += '.';
        parts.push(msg);
    }

    return parts.join(' ');
};

export const generateRootPathologyText = (data) => {
    const parts = [];

    if (data.periradicular && data.periradicular !== 'Normal') {
        parts.push(`Periradicular bone: ${data.periradicular}.`);
    }
    if (data.pdl && data.pdl !== 'Normal') {
        parts.push(`PDL Space: ${data.pdl}.`);
    }
    if (data.periapical && data.periapical !== 'Normal') {
        parts.push(`Periapical assessment: ${data.periapical}.`);
    }

    return parts.join(' ');
};

export const generateDynamicRootText = (data) => {
    if (!data.rootsList || data.rootsList.length === 0) return '';

    const parts = [];
    data.rootsList.forEach(root => {
        let rootPart = `${root.name}:`;
        if (root.development) rootPart += ` Development: ${root.development}.`;

        // Handle Shape array or string
        if (root.shape) {
            const shapes = Array.isArray(root.shape) ? root.shape.join(', ') : root.shape;
            if (shapes) rootPart += ` Shape: ${shapes}.`;
        }

        if (root.curvature) rootPart += ` Curvature: ${root.curvature}.`;
        if (root.length) rootPart += ` Length: ${root.length}mm.`;

        parts.push(rootPart);
    });

    return parts.join(' ');
};

// --- MANDIBLE SPECIFIC GENERATORS ---

export const generateIANCText = (data) => {
    const parts = [];

    // Bifurcation
    if (data.bifurcation && data.bifurcation !== 'Absent') {
        parts.push(`Canal configuration: ${data.bifurcation}.`);
    }

    // Crown relations
    if (data.crownRelations && data.crownRelations.length > 0) {
        data.crownRelations.forEach((cr, idx) => {
            let crText = `Crown Relation ${idx + 1}:`;
            const measures = [];
            if (cr.occlusal) measures.push(`Occlusal 3rd ${cr.occlusal}mm`);
            if (cr.mid) measures.push(`Mid 3rd ${cr.mid}mm`);
            if (cr.cervical) measures.push(`Cervical 3rd ${cr.cervical}mm`);

            if (measures.length > 0) {
                crText += ` Inferiorly at ${measures.join(', ')}.`;
                parts.push(crText);
            }
        });
    }

    // Root relations
    if (data.rootRelations && data.rootRelations.length > 0) {
        data.rootRelations.forEach(rr => {
            let rrText = `${rr.name}:`;
            if (rr.course) rrText += ` Course ${rr.course.toLowerCase()}.`;
            if (rr.cortication) rrText += ` Cortication ${rr.cortication.toLowerCase()}.`;
            if (rr.bonyBarrier) rrText += ` Bony barrier: ${rr.bonyBarrier}.`;
            if (rr.compression && rr.compression !== 'Absent') rrText += ` Compression: ${rr.compression}.`;
            if (rr.displacement && rr.displacement !== 'Absent') rrText += ` Displacement: ${rr.displacement}.`;

            // Proximity
            const prox = [];
            if (rr.proximityCervical) prox.push(`Cervical 3rd ${rr.proximityCervical}mm`);
            if (rr.proximityMid) prox.push(`Mid 3rd ${rr.proximityMid}mm`);
            if (rr.proximityApical) prox.push(`Apical 3rd ${rr.proximityApical}mm`);
            if (rr.proximityApex) prox.push(`Apex ${rr.proximityApex}mm`);

            if (prox.length > 0) {
                rrText += ` Proximity to root at ${prox.join(', ')}.`;
            }

            parts.push(rrText);
        });
    }

    return parts.join(' ');
};

export const generateMandibleAdjacentText = (data, impactionType) => {
    const parts = [];

    // Pell & Gregory (Ramus/Occlusal)
    if (data.ramusRelation) {
        // Extract just the class if needed, or print full. The options are "Class I: ...". 
        // Let's just say "Ramus relationship: Class I (Sufficient space...)"
        parts.push(`Ramus relationship: ${data.ramusRelation}.`);
    }
    if (data.pellDepth) {
        // Options are "Class A (Shallow)..."
        parts.push(`Depth of impaction: ${data.pellDepth}.`);
    }

    // Specific Impaction Type Relations
    if (impactionType === 'Vertical') {
        if (data.verticalOcclusal) parts.push(`Occlusal plane relation: ${data.verticalOcclusal}.`);
        if (data.verticalAxis) parts.push(`Long axis relation: ${data.verticalAxis}.`);
    }
    else if (impactionType === 'Mesioangular') {
        if (data.mesioContact) parts.push(`Contact with distal of 7: ${data.mesioContact}.`);
        if (data.mesioDamage && data.mesioDamage !== 'None') parts.push(`Damage to 7: ${data.mesioDamage}.`);
    }
    else if (impactionType === 'Distoangular') {
        if (data.distoContact) parts.push(`Contact with distal of 7: ${data.distoContact}.`);
        if (data.distoFeature) parts.push(`Feature: ${data.distoFeature}.`);
    }
    else if (impactionType === 'Transverse') {
        if (data.transverseOrientation) parts.push(`Orientation: ${data.transverseOrientation}.`);
    }

    return parts.join(' ');
};

export const generateMeasurementsText = (data, section) => {
    const parts = [];

    // Crown Measurements
    if ((!section || section === 'crown') && data.crownMd) {
        parts.push(`The mesiodistal width of the crown at the CEJ is ${data.crownMd} mm.`);
    }

    // Crestal Bone
    if ((!section || section === 'crestal') && data.crestalBone && data.crestalBone !== 'No significant loss') {
        parts.push(`Crestal bone status: ${data.crestalBone}.`);
    }

    return parts.join(' ');
};

export const generateMandiblePositionText = (data) => {
    // Mandible Position might have small diffs, but usually same.
    // We can export generic one or wrap it.
    // For now, let's just make sure we export what we need. 
    // Usually existing generatePositionSectionText is enough.
    return '';
};
